import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';

import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import { OptionType } from '@/ui/components/Form/Select/types';

import JurisdictionRepository from '../../repositories/JurisdictionRepository';
import NonBillableReasonRepository from '../../repositories/NonBillableReasonRepository';
import StaffRepository from '../../repositories/StaffRepository';
import { FieldEdit, FieldEditTypes, FieldEditTypeSlugs } from '../RowEditor/types';

interface Props {
  fieldEdit: FieldEdit;
  onChange: (newFieldEdit: FieldEdit) => void;
  onRemove: (id: string) => void;
}

const FieldEditTypeOptions: Record<string, OptionType> = {};
FieldEditTypes.forEach((editType) => {
  FieldEditTypeOptions[editType.slug] = {
    label: editType.name,
    value: editType.slug
  };
});

const RowEditorField = ({ fieldEdit, onRemove, onChange }: Props): JSX.Element => {
  const [providerOptions, setProviderOptions] = useState<OptionType[]>([]);
  const [nonBillableOptions, setNonBillableOptions] = useState<OptionType[]>([]);
  const NonBillableReasonRepo = NonBillableReasonRepository();
  const [jurisdictionOptions, setJurisdictionOptions] = useState<OptionType[]>([]);
  const StaffRepo = StaffRepository();
  const JurisdictionRepo = JurisdictionRepository();

  const destinationOptions = [
    { label: 'Office Ally', value: 'office_ally' },
    { label: 'Candid', value: 'candid' }
  ];
  useEffect(() => {
    if (fieldEdit.edit_type === 'set_rendering_provider') {
      StaffRepo.getStaffList().then((staff) => {
        setProviderOptions(
          staff.map((provider) => ({
            label: `${provider.first_name} ${provider.last_name}`,
            value: provider.id
          }))
        );
      });
    }
    if (fieldEdit.edit_type === 'set_jurisdiction') {
      JurisdictionRepo.getJurisdictionList().then((jurisdictions) => {
        setJurisdictionOptions(
          jurisdictions.map((jurisdiction) => ({
            label: jurisdiction.name,
            value: jurisdiction.name
          }))
        );
      });
    }
    if (fieldEdit.edit_type === FieldEditTypeSlugs.mark_unbillable) {
      NonBillableReasonRepo.list().then((reasons) => {
        setNonBillableOptions(
          reasons.map((reason) => ({
            label: reason.name,
            value: reason.id
          }))
        );
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldEdit.edit_type]);

  const renderField = (fieldEdit: FieldEdit) => {
    switch (fieldEdit.edit_type) {
      case 'set_rendering_provider':
        return (
          <div>
            <AutocompleteSingleSelect
              onChange={(option) => {
                onChange({
                  ...fieldEdit,
                  value: option?.value || ''
                });
              }}
              placeholder="Select a provider"
              label="Provider"
              options={providerOptions}
              selectedOption={
                providerOptions.find((provider) => provider.value === fieldEdit.value) || {
                  label: '',
                  value: ''
                }
              }
            />
          </div>
        );
      case 'set_jurisdiction':
        return (
          <div>
            <AutocompleteSingleSelect
              onChange={(option) => {
                onChange({
                  ...fieldEdit,
                  value: option?.value || ''
                });
              }}
              placeholder="Select a Jurisdiction"
              label="Jurisdiction"
              options={jurisdictionOptions}
              selectedOption={
                jurisdictionOptions.find((state) => state.value === fieldEdit.value) || {
                  label: '',
                  value: ''
                }
              }
            />
          </div>
        );
      case 'set_destination':
        return (
          <div>
            <AutocompleteSingleSelect
              onChange={(option) => {
                onChange({
                  ...fieldEdit,
                  value: option?.value || ''
                });
              }}
              placeholder="Select a Destination"
              label="Destination"
              options={destinationOptions}
              selectedOption={
                destinationOptions.find((destination) => destination.value === fieldEdit.value) || {
                  label: '',
                  value: ''
                }
              }
            />
          </div>
        );
      case FieldEditTypeSlugs.mark_unbillable:
        return (
          <div>
            <AutocompleteSingleSelect
              onChange={(option) => {
                onChange({
                  ...fieldEdit,
                  value: option?.value || ''
                });
              }}
              placeholder="Select a reason"
              label="Reason"
              options={nonBillableOptions}
              selectedOption={
                nonBillableOptions.find((reason) => reason.value === fieldEdit.value) || {
                  label: '',
                  value: ''
                }
              }
            />
          </div>
        );
      default:
        return (
          <div className="mt-2">
            <label
              htmlFor={`fieldEditValue-${fieldEdit.id}`}
              className="text-gray-600 text-sm mt-2">
              * Value:
            </label>
            <input
              id={`fieldEditValue-${fieldEdit.id}`}
              value={fieldEdit.value}
              onChange={(e) => {
                onChange({
                  ...fieldEdit,
                  value: e.target.value
                });
              }}
              className="p-2 w-full text-sm text-gray-600 border border-gray-300 rounded focus:outline-none focus:border-blue-500 transition duration-200 ease-in-out"
            />
          </div>
        );
    }
  };

  return (
    <div
      className="row-editor-field mt-4 bg-white px-2 py-2 rounded shadow-sm border border-gray-300 transition duration-200 ease-in-out"
      id={`row-editor-field-${fieldEdit.id}`}>
      {/* form */}
      <div className="grid grid-cols-3 gap-4">
        {/* Type */}
        <div>
          <AutocompleteSingleSelect
            onChange={(option) => {
              onChange({
                ...fieldEdit,
                edit_type: option?.value || ''
              });
            }}
            placeholder="Select a type"
            label="Type"
            options={Object.values(FieldEditTypeOptions)}
            selectedOption={FieldEditTypeOptions[fieldEdit.edit_type] || { label: '', value: '' }}
          />
        </div>

        {/* Value */}
        {renderField(fieldEdit)}

        {/* Remove */}
        <div className="mt-2">
          <button
            onClick={() => onRemove(fieldEdit.id)}
            className="bg-gray-400 text-white px-2 py-2 rounded mt-6">
            Remove
          </button>
        </div>
      </div>
      <div>
        {/* Description */}
        <div>
          <p
            id={`fieldEditDescription-${fieldEdit.id}`}
            className="p-2 text-sm text-gray-700 transition duration-200 ease-in-out">
            {FieldEditTypes.find((editType) => editType.slug === fieldEdit.edit_type)?.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RowEditorField;
